import type { CardSummary as Card } from "@hobbyshopify/backend-client";
import { useState } from "react";
import { cardTitle } from "../core/primitives/card-display";

export type StockChange = {
  quantity?: number;
  kind?: "ADJUSTED" | "SHRINKAGE";
  reason?: string;
  trackStock?: boolean;
};

/**
 * Setting a card's stock level.
 *
 * The number typed is the **new total**, not a change to apply — a merchant
 * counting a shelf knows what is there, not what the difference is. The ledger
 * works out the difference and records it, which is what keeps the stored count
 * a sum of movements rather than a value somebody assigned.
 *
 * The reason is asked for rather than inferred, and the two kinds are offered
 * separately, because "we miscounted" and "we lost six" answer different
 * questions later and the model already distinguishes them.
 */
export function StockLevelModal({
  card,
  busy,
  error,
  onSave,
  onCancel,
}: {
  card: Card;
  busy?: boolean;
  error?: string | null;
  onSave: (change: StockChange) => void;
  onCancel: () => void;
}) {
  const tracked = card.trackStock;
  const [quantity, setQuantity] = useState(tracked ? String(card.available) : "1");
  const [kind, setKind] = useState<"ADJUSTED" | "SHRINKAGE">("ADJUSTED");
  const [reason, setReason] = useState("");

  const parsed = Number(quantity);
  const quantityValid = Number.isInteger(parsed) && parsed >= 0;
  const delta = quantityValid ? parsed - (tracked ? card.available : 0) : 0;

  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <div className="modal relative w-full max-w-sm p-6">
        <h2 className="mb-1 text-base font-semibold text-white">
          {tracked ? "Set stock level" : "Start tracking stock"}
        </h2>
        <p className="mb-4 truncate text-sm text-muted">{cardTitle(card)}</p>

        <div className="space-y-3">
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">On hand</div>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className={`field ${quantityValid ? "" : "border-danger focus:border-danger"}`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              aria-invalid={!quantityValid}
            />
            {quantityValid ? (
              <p className="mt-1 text-xs text-faint">
                {tracked
                  ? delta === 0
                    ? `Currently ${card.available}. No change.`
                    : `Currently ${card.available} — this records ${delta > 0 ? `+${delta}` : delta}.`
                  : "The shop is not counting this card yet. Saving starts."}
              </p>
            ) : (
              <p className="field-error text-xs">Enter a whole number, zero or more.</p>
            )}
          </div>

          {tracked && delta < 0 && (
            <div>
              <div className="mb-1 block text-xs font-medium text-muted">Why fewer?</div>
              <select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="ADJUSTED">We miscounted</option>
                <option value="SHRINKAGE">Lost, stolen or damaged</option>
              </select>
              {/* Two kinds rather than one, so "what did we lose this month" is a
                  query rather than a trawl through free text. */}
              <p className="mt-1 text-xs text-faint">
                Kept apart so losses can be totalled separately from corrections.
              </p>
            </div>
          )}

          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Note</div>
            <input
              className="field"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={delta < 0 ? "Water damage, shelf recount…" : "Recount, restocked…"}
            />
            <p className="mt-1 text-xs text-faint">
              Recorded against the change. A correction with no reason is hard to tell from a bug later.
            </p>
          </div>
        </div>

        {error && <p className="note note-danger mt-3 text-danger">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ quantity: parsed, kind, reason: reason.trim() || undefined })}
            disabled={busy || !quantityValid || (tracked && delta === 0)}
            className="btn-primary flex-1"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>

        {/* Only from zero, and the button says why rather than being disabled
            with no explanation — a count that vanished while the lots stayed is
            a state nothing else knows how to read. */}
        {tracked && (
          <button
            type="button"
            onClick={() =>
              onSave({ quantity: 0, kind: "ADJUSTED", reason: reason.trim() || "Stopped tracking", trackStock: false })
            }
            disabled={busy}
            className="btn-ghost mt-3 w-full"
          >
            Stop counting this card
          </button>
        )}
      </div>
    </div>
  );
}
